/**
 * Locale-aware date and number formatters.
 *
 * All formatters use the built-in Intl API so no extra runtime dependency is
 * required.  Pass the active i18n language tag (e.g. "ka", "ar", "zh-CN") and
 * the function returns a formatted string in the appropriate locale convention.
 *
 * The "ka" (Georgian) locale is not widely supported by Intl yet — we fall back
 * to "en-GB" for Georgian so date order (DD/MM/YYYY) is familiar.
 */

const LOCALE_MAP: Record<string, string> = {
  ka: 'en-GB',
  en: 'en-US',
  ru: 'ru-RU',
  tr: 'tr-TR',
  az: 'az-AZ',
  de: 'de-DE',
  ar: 'ar-SA',
  uk: 'uk-UA',
  'zh-CN': 'zh-CN',
};

function resolveLocale(lang: string): string {
  return LOCALE_MAP[lang] ?? lang;
}

export function fmtDate(
  date: Date | string | number,
  lang: string,
  opts: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' },
): string {
  const d = date instanceof Date ? date : new Date(date);
  return new Intl.DateTimeFormat(resolveLocale(lang), opts).format(d);
}

export function fmtDateShort(date: Date | string | number, lang: string): string {
  return fmtDate(date, lang, { year: 'numeric', month: '2-digit', day: '2-digit' });
}

export function fmtDateRelative(date: Date | string | number, lang: string): string {
  const d = date instanceof Date ? date : new Date(date);
  const diffMs = d.getTime() - Date.now();
  const diffDays = Math.round(diffMs / 86_400_000);

  const rtf = new Intl.RelativeTimeFormat(resolveLocale(lang), { numeric: 'auto' });

  if (Math.abs(diffDays) < 1) {
    const diffHours = Math.round(diffMs / 3_600_000);
    if (Math.abs(diffHours) < 1) {
      const diffMins = Math.round(diffMs / 60_000);
      return rtf.format(diffMins, 'minute');
    }
    return rtf.format(diffHours, 'hour');
  }
  if (Math.abs(diffDays) < 30) return rtf.format(diffDays, 'day');
  if (Math.abs(diffDays) < 365) return rtf.format(Math.round(diffDays / 30), 'month');
  return rtf.format(Math.round(diffDays / 365), 'year');
}

export function fmtNumber(
  value: number,
  lang: string,
  opts: Intl.NumberFormatOptions = {},
): string {
  return new Intl.NumberFormat(resolveLocale(lang), opts).format(value);
}

export function fmtCurrency(value: number, lang: string, currency: string): string {
  return fmtNumber(value, lang, { style: 'currency', currency, minimumFractionDigits: 2 });
}

export function fmtPercent(value: number, lang: string): string {
  return fmtNumber(value, lang, { style: 'percent', maximumFractionDigits: 1 });
}

export function fmtShortDate(date: Date | string | number, lang: string): string {
  return fmtDateShort(date, lang);
}

export function fmtDateTime(date: Date | string | number, lang: string): string {
  return fmtDate(date, lang, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
